import api from './api'

/**
 * Portal-facing API helpers. All endpoints below are public (permitAll) per the
 * backend SecurityConfig, so they work for logged-out visitors too.
 * Responses use the backend ApiResponse<T> envelope: { success, message, data }.
 */

export interface KnowledgeArticle {
  id: number
  title: string
  slug: string
  excerpt?: string
  content?: string
  featuredImage?: string
  categoryName?: string
  categoryId?: number
  authorName?: string
  viewCount?: number
  likeCount?: number
  isFeatured?: boolean
  isPublished?: boolean
  publishedAt?: string
  createdAt?: string
  tags?: string[]
  /** Language actually served by the API — the requested one if a translation exists, else "en". */
  matchedLanguage?: string
  /** Languages this article is available in; always includes "en" (the canonical row). */
  availableLanguages?: string[]
}

export interface PageResponse<T> {
  content: T[]
  totalElements: number
  totalPages: number
  number: number
  size: number
}

/** Published knowledge articles (public). */
export async function getPublishedArticles(page = 0, size = 6) {
  const res = await api.get('/knowledge/articles', { params: { page, size } })
  return (res.data?.data ?? res.data) as PageResponse<KnowledgeArticle>
}

/** Articles filtered by category id (public). */
export async function getArticlesByCategory(categoryId: number, page = 0, size = 6) {
  const res = await api.get(`/knowledge/categories/${categoryId}/articles`, { params: { page, size } })
  return (res.data?.data ?? res.data) as PageResponse<KnowledgeArticle>
}

/** Knowledge categories (public). */
export async function getKnowledgeCategories() {
  const res = await api.get('/knowledge/categories')
  return (res.data?.data ?? res.data) as { id: number; name: string; slug: string }[]
}

/** Article by slug (public). */
export async function getArticleBySlug(slug: string) {
  const res = await api.get(`/knowledge/articles/${slug}`)
  return (res.data?.data ?? res.data) as KnowledgeArticle
}

export interface Campaign {
  id: number
  title: string
  slug?: string
  description?: string
  campaignType?: string
  status?: string
  startDate?: string
  endDate?: string
  location?: string
  targetAmount?: number
  collectedAmount?: number
  coverImage?: string
  isFeatured?: boolean
}

/** Public campaigns. */
export async function getCampaigns(page = 0, size = 6) {
  const res = await api.get('/campaigns', { params: { page, size } })
  return (res.data?.data ?? res.data) as PageResponse<Campaign>
}

/** Featured campaigns. */
export async function getFeaturedCampaigns() {
  const res = await api.get('/campaigns/featured')
  return (res.data?.data ?? res.data) as Campaign[]
}

export interface NewsItem {
  id: number
  title: string
  slug?: string
  summary?: string
  publishedAt?: string
  source?: string
  url?: string
  imageUrl?: string | null
  category?: string
  isPinned?: boolean
  sortOrder?: number
}

/** TB news (public). */
export async function getNews(page = 0, size = 5) {
  const res = await api.get('/news', { params: { page, size } })
  return (res.data?.data ?? res.data) as PageResponse<NewsItem> | NewsItem[]
}